import {Alert, Badge, Button, Card, Spinner, Table} from "react-bootstrap"

import type {PlanningTodo} from "../RoomPowerPlanningOverviewComponent"
import {seatSpacingPolicy} from "./display"
import type {PlanDocument, Room} from "./types"

export default function RoomPlanDocumentsComponent({
    room,
    documents,
    todos,
    openingDocumentId,
    onOpenPdf,
}: {
    room: Room
    documents: PlanDocument[]
    todos: PlanningTodo[]
    openingDocumentId: string | null
    onOpenPdf: (document: PlanDocument) => void
}) {
    return <>
        <Card className="mb-3">
            <Card.Header><strong>Sitzplan-PDFs</strong></Card.Header>
            <Card.Body>
                {documents.length === 0
                    ? <Alert variant="warning" className="mb-0">
                        Für diesen Hörsaal ist noch kein PDF hinterlegt.
                    </Alert>
                    : <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead><tr>
                                <th>Datei</th>
                                <th>Status</th>
                                <th>Erkannte Sitzplatzaufteilungen</th>
                                <th>Größe</th>
                                <th/>
                            </tr></thead>
                            <tbody>{documents.map(document =>
                                <tr key={document._id}>
                                    <td>
                                        <strong>{document.filename}</strong>
                                        {document.sourceLabel &&
                                            <div className="small text-muted">
                                                {document.sourceLabel}
                                            </div>}
                                    </td>
                                    <td><Badge bg={
                                        document.interpretationStatus === "VERIFIED"
                                            ? "success"
                                            : document.interpretationStatus ===
                                            "NEEDS_REVIEW"
                                                ? "warning" : "secondary"
                                    }>
                                        {document.interpretationStatus}
                                    </Badge></td>
                                    <td>
                                        {document.variants.length === 0
                                            ? "—"
                                            : document.variants.map(variant =>
                                                `${seatSpacingPolicy(variant.variant)}: ` +
                                                `${variant.examCapacity}`).join(", ")}
                                    </td>
                                    <td>
                                        {(document.bytes / 1024)
                                            .toLocaleString("de-DE", {
                                                maximumFractionDigits: 0,
                                            })} KB
                                    </td>
                                    <td className="text-end">
                                        <Button size="sm" variant="outline-primary"
                                            disabled={openingDocumentId ===
                                                document._id}
                                            onClick={() => onOpenPdf(document)}>
                                            {openingDocumentId === document._id
                                                ? <Spinner size="sm"/>
                                                : <><i className=
                                                    "bi bi-box-arrow-up-right me-2"/>
                                                PDF öffnen</>}
                                        </Button>
                                    </td>
                                </tr>)}</tbody>
                        </Table>
                    </div>}
            </Card.Body>
        </Card>
        <Card>
            <Card.Header>
                <strong>To-dos für {room.building?.identifier}{" "}
                    {room.identifier}</strong>
            </Card.Header>
            <Card.Body>
                {todos.length === 0
                    ? <Alert variant="success" className="mb-0">
                        Für diesen Hörsaal sind derzeit keine offenen
                        Planungspunkte erkannt.
                    </Alert>
                    : <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead><tr>
                                <th>Bereich</th>
                                <th>Aufgabe</th>
                                <th>Hinweis</th>
                                <th className="text-end">Offen</th>
                            </tr></thead>
                            <tbody>{todos.map(todo =>
                                <tr key={`${todo.category}-${todo.title}`}>
                                    <td><Badge bg="secondary">
                                        {todo.category === "SOURCE"
                                            ? "Quelle"
                                            : todo.category === "GEOMETRY"
                                                ? "Geometrie"
                                                : todo.category === "MEASUREMENT"
                                                    ? "Vermessung"
                                                    : todo.category === "POWER"
                                                        ? "Elektro"
                                                        : "Prüfungsplätze"}
                                    </Badge></td>
                                    <td><strong>{todo.title}</strong></td>
                                    <td>{todo.detail}</td>
                                    <td className="text-end">{todo.count}</td>
                                </tr>)}</tbody>
                        </Table>
                    </div>}
            </Card.Body>
        </Card>
    </>
}
